import type { MutableRefObject } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import useBoolean from './use-boolean'

export type Options<T> = {
  dom?: T | (() => T) | null
  onEnter?: () => void
  onLeave?: () => void
}

export default function <T extends HTMLElement | SVGElement = HTMLElement>(
  options?: Options<T>
): [boolean | undefined, MutableRefObject<T>?] {
  const { dom, onEnter, onLeave } = options || {}

  const element = useRef<T>(null)

  const onEnterRef = useRef(onEnter)
  onEnterRef.current = onEnter

  const onLeaveRef = useRef(onLeave)
  onLeaveRef.current = onLeave

  const [state, setHovered] = useBoolean()

  const onMouseEnter = useCallback(() => {
    // eslint-disable-next-line no-console
    // console.log(`useHover onMouseEnter`)
    if (onEnterRef.current) onEnterRef.current()
    setHovered.on()
  }, [setHovered])

  const onMouseLeave = useCallback(() => {
    // eslint-disable-next-line no-console
    // console.log(`useHover onMouseLeave`)
    if (onLeaveRef.current) onLeaveRef.current()
    setHovered.off()
  }, [setHovered])

  useEffect(() => {
    // eslint-disable-next-line no-console
    // console.log(`useHover add listener`)
    const passedInElement = typeof dom === 'function' ? dom() : dom
    // 如果 传入dom
    if (passedInElement) {
      passedInElement.addEventListener('mouseenter', onMouseEnter)
      passedInElement.addEventListener('mouseleave', onMouseLeave)
      return () => {
        passedInElement.removeEventListener('mouseenter', onMouseEnter)
        passedInElement.removeEventListener('mouseleave', onMouseLeave)
      }
    }
    const node = element.current
    if (node) {
      node.addEventListener('mouseenter', onMouseEnter)
      node.addEventListener('mouseleave', onMouseLeave)
      return () => {
        node.removeEventListener('mouseenter', onMouseEnter)
        node.removeEventListener('mouseleave', onMouseLeave)
      }
    }
    return
  }, [element, dom, onMouseLeave, onMouseEnter])

  if (dom) {
    return [!!state]
  }

  return [!!state, element as MutableRefObject<T>]
}
